import { AUTOPAY_CONSENT_VERSION, type AutopayEnrollment, type AutopayPreview, type AutopayRunStatus } from "@shyne/shared/api";
import { api } from "@/lib/api";
import { activeOrganizationId } from "@/lib/active-organization";
import { RECIPIENT_KIND } from "@/services/stripe-subs-payments.service";

export type AutopayRecipientType = "individual" | "subs";
export type AutopayLastRunStatus = AutopayRunStatus;

export interface RecipientAutopayState {
  recipientType: AutopayRecipientType;
  recipientId: string;
  enabled: boolean;
  paused: boolean;
  pauseReason: string | null;
  lastRunAt: string | null;
  lastRunStatus: AutopayLastRunStatus | null;
  lastRunErrorMessage: string | null;
  lastRunAmountCents: number | null;
}

export type { AutopayPreview };

export function autopayStateKey(type: AutopayRecipientType, id: string): string {
  return `${type}:${id}`;
}

const acting = () => ({ actingOrganization: activeOrganizationId() });

const toState = (enrollment: AutopayEnrollment): RecipientAutopayState => ({
  recipientType: enrollment.recipient.kind === "subcontractor" ? "subs" : "individual",
  recipientId: enrollment.recipient.id,
  enabled: enrollment.enabled,
  paused: enrollment.paused,
  pauseReason: enrollment.pauseReason,
  lastRunAt: enrollment.lastRunAt,
  lastRunStatus: enrollment.lastRunStatus,
  lastRunErrorMessage: enrollment.lastRunError,
  lastRunAmountCents: enrollment.lastRunAmountCents,
});

export async function getAutopayStates(): Promise<RecipientAutopayState[]> {
  const { items } = await api.autopay.list(acting());
  return items.map(toState);
}

export async function getAutopayPreview(recipientType: AutopayRecipientType, recipientId: string): Promise<AutopayPreview> {
  return api.autopay.preview(RECIPIENT_KIND[recipientType], recipientId, acting());
}

export async function enableAutopay(recipientType: AutopayRecipientType, recipientId: string): Promise<RecipientAutopayState> {
  return toState(await api.autopay.enable(RECIPIENT_KIND[recipientType], recipientId, { ...acting(), consentAccepted: true, consentVersion: AUTOPAY_CONSENT_VERSION }));
}

export async function disableAutopay(recipientType: AutopayRecipientType, recipientId: string): Promise<RecipientAutopayState> {
  return toState(await api.autopay.disable(RECIPIENT_KIND[recipientType], recipientId, acting()));
}
